// utility functions for coffee rating logic

import { RatingValues, CategoryControlConfig } from './types';

/**
 * clamp a rating value to valid range (1-10)
 * supports both integer and decimal precision
 */
export const clampRating = (value: number, precision: 'integer' | 'decimal' = 'decimal'): number => {
    const clamped = Math.max(1, Math.min(10, value || 5));
    if (precision === 'integer') {
        return Math.round(clamped);
    }
    // decimal precision: round to 1 decimal place
    return Math.round(clamped * 10) / 10;
};

/**
 * convert centered slider position to rating (1-10)
 * position: -maxRange to +maxRange, where 0 = center = rating 10
 * formula: rating = max(1, 10 - abs(position) * 2)
 */
export const positionToRating = (position: number, maxRange: number = 5): number => {
    const clampedPos = Math.max(-maxRange, Math.min(maxRange, position));
    return Math.max(1, 10 - Math.abs(clampedPos) * 2);
};

/**
 * convert rating (1-10) to centered slider position
 * rating 10 = position 0 (center)
 * rating 1 = position ±maxRange
 */
export const ratingToPosition = (rating: number, maxRange: number = 5): number => {
    const clampedRating = clampRating(rating);
    if (clampedRating === 10) return 0;
    // rating decreases as we move away from center
    // position = (10 - rating) / 2, but we need to determine sign
    // for simplicity, we'll use negative positions for lower ratings
    const distance = (10 - clampedRating) / 2;
    return -Math.min(distance, maxRange);
};

/**
 * initialize default ratings for a coffee type
 * sets all categories to default value (5)
 */
export const initializeRatings = (
    categories: { [key: string]: { weight: number; required: boolean } },
    existingRatings?: RatingValues
): RatingValues => {
    const ratings: RatingValues = {};
    
    Object.keys(categories).forEach(category => {
        const existingRating = existingRatings?.[category];
        const existingValue = existingRating ? parseFloat(String(existingRating)) : NaN;
        
        // preserve existing valid ratings (1-10)
        if (!isNaN(existingValue) && existingValue >= 1 && existingValue <= 10) {
            ratings[category] = existingValue;
        } else {
            // default to 5
            ratings[category] = 5;
        }
    });
    
    return ratings;
};

/**
 * get category control configs sorted by weight (descending)
 */
export const getSortedCategoryConfigs = (
    categories: { [key: string]: { weight: number; required: boolean } },
    centeredCategories?: { [category: string]: { leftLabel: string; rightLabel: string; maxRange?: number } }
): CategoryControlConfig[] => {
    return Object.entries(categories)
        .map(([category, details]) => {
            const centered = centeredCategories?.[category];
            return {
                category,
                weight: details.weight,
                required: details.required,
                isCentered: !!centered,
                leftLabel: centered?.leftLabel,
                rightLabel: centered?.rightLabel,
                maxRange: centered?.maxRange || 5,
            };
        })
        .sort((a, b) => b.weight - a.weight);
};

// tokens that classify a rate-category key as sugar- or milk-related
const SUGAR_TOKENS = [/sugar/i, /sweet/i];
const MILK_TOKENS = [/milk/i, /foam/i, /dairy/i];

const matchesAny = (key: string, patterns: RegExp[]): boolean =>
    patterns.some((re) => re.test(key));

/**
 * drop rate categories that don't apply to the chosen coffee. when
 * `coffeeFormat.sugar` is undefined, sugar/sweetness sliders are removed;
 * same for `coffeeFormat.milk` and milk/foam/dairy sliders. anything not
 * clearly a sugar or milk dimension is preserved.
 */
export const filterApplicableCategories = <T extends { [key: string]: { weight: number; required: boolean } }>(
    rateCategories: T,
    coffeeFormat?: { sugar?: unknown; milk?: unknown } | null,
): T => {
    const sugarApplies = !!coffeeFormat && coffeeFormat.sugar !== undefined;
    const milkApplies = !!coffeeFormat && coffeeFormat.milk !== undefined;
    if (sugarApplies && milkApplies) return rateCategories;

    const filtered: { [key: string]: { weight: number; required: boolean } } = {};
    Object.entries(rateCategories).forEach(([key, value]) => {
        if (!sugarApplies && matchesAny(key, SUGAR_TOKENS)) return;
        if (!milkApplies && matchesAny(key, MILK_TOKENS)) return;
        filtered[key] = value;
    });
    return filtered as T;
};

/**
 * format rating value for display
 */
export const formatRating = (value: number, precision: 'integer' | 'decimal' = 'decimal'): string => {
    if (precision === 'integer') {
        return Math.round(value).toString();
    }
    return value.toFixed(1);
};
